/**
 * crmlive: ask the `crm-facts` edge function to fill this org's blanks from
 * the Tohund Guide CRM, at most once a day per org on this device.
 *
 * Called by the pending list (useGetStarted) and the ROC map's empty state
 * when the date of incorporation is missing. Both may ask at once, so the
 * call is shared. The function itself decides whether the org is a Tohund
 * Guide client; for anybody else it answers `not_a_client` and writes nothing.
 *
 * Quiet by design: this runs behind a page, so a failure is logged, never
 * toasted. The pending list's own row still asks the person.
 */
import { supabase } from '@/db/supabase';
import { afterFirstPaint } from '@/shared/lib/afterFirstPaint';
import { edgeErrorMessage } from '@/shared/utils/edgeError';
import type { CrmSweepSummary } from './crmSweepNotice';

export { sweepNotice, type CrmSweepSummary } from './crmSweepNotice';

export type CrmFillStatus =
    | 'filled' | 'nothing_to_fill' | 'not_found' | 'ambiguous' | 'not_a_client' | 'checked_recently';

export interface CrmFillResult {
    status: CrmFillStatus;
    filled?: string[];
}

/** Fired with `{ orgId }` when the CRM filled something, so open pages reload. */
export const CRM_FILLED_EVENT = 'karr:crm-filled';

const DAY_MS = 24 * 3600 * 1000;
const inFlight = new Map<string, Promise<CrmFillResult | null>>();
const storeKey = (orgId: string) => `karr.crmfill.${orgId}`;

function askedRecently(orgId: string): boolean {
    try {
        const at = Number(localStorage.getItem(storeKey(orgId)) ?? 0);
        return at > 0 && Date.now() - at < DAY_MS;
    } catch { return false; }
}

function noteAsked(orgId: string) {
    try { localStorage.setItem(storeKey(orgId), String(Date.now())); } catch { /* private window */ }
}

export function askCrmOnce(orgId: string): Promise<CrmFillResult | null> {
    const running = inFlight.get(orgId);
    if (running) return running;
    if (askedRecently(orgId)) return Promise.resolve(null);
    const p = (async () => {
        // fastphone-2: 3 s on a cold open, for a date no first screen draws.
        await afterFirstPaint();
        const { data, error } = await supabase.functions.invoke('crm-facts', { body: { org_id: orgId } });
        noteAsked(orgId);
        if (error) {
            console.warn('crm-facts:', await edgeErrorMessage(error));
            return null;
        }
        const r = data as CrmFillResult & { ok?: boolean };
        if (r?.status === 'filled') window.dispatchEvent(new CustomEvent(CRM_FILLED_EVENT, { detail: { orgId } }));
        return r ?? null;
    })().catch((e) => { console.warn('crm-facts:', e); return null; });
    inFlight.set(orgId, p);
    void p.finally(() => inFlight.delete(orgId));
    return p;
}

/* ── crmsweep: every Tohund Guide client at once ───────────────────────── */

/** Tohund Guide, the practice whose CRM this is. Only its office sweeps. */
export const TOHUND_PRACTICE_ORG_ID = '288b7f1e-9563-4d4f-b648-c645eb04f46e';

const SWEEP_KEY = 'karr.crmsweep.at';
let sweeping: Promise<void> | null = null;

/**
 * Fill every Tohund Guide client's missing date from the CRM: one call per
 * time-boxed batch, repeated while the function says more remain. Once a day
 * on this device. Calls `onDone` with the total filled and the last summary.
 */
export function runCrmSweepDaily(onDone: (filled: number, s: CrmSweepSummary | null) => void): Promise<void> {
    if (sweeping) return sweeping;
    try {
        const at = Number(localStorage.getItem(SWEEP_KEY) ?? 0);
        if (at > 0 && Date.now() - at < DAY_MS) return Promise.resolve();
        localStorage.setItem(SWEEP_KEY, String(Date.now()));
    } catch { /* private window: sweep anyway, the server's own recheck limit holds */ }

    sweeping = (async () => {
        let filled = 0;
        let last: CrmSweepSummary | null = null;
        // A safety cap: 12 batches of ~80 s is far more than the client list needs.
        for (let round = 0; round < 12; round += 1) {
            const { data, error } = await supabase.functions.invoke('crm-facts', { body: { sweep: true } });
            if (error) { console.warn('crm-facts sweep:', await edgeErrorMessage(error)); break; }
            const r = data as { processed?: number; remaining?: number; summary?: CrmSweepSummary };
            last = r.summary ?? last;
            filled += r.summary?.filled_this_run ?? 0;
            if (filled) window.dispatchEvent(new CustomEvent(CRM_FILLED_EVENT, { detail: { orgId: null } }));
            if (!r.remaining || !r.processed) break;
        }
        onDone(filled, last);
    })().catch((e) => { console.warn('crm-facts sweep:', e); }).finally(() => { sweeping = null; });
    return sweeping;
}
